import { useAuth } from 'context/AuthContext';
import { BotIcon, PencilIcon } from 'lucide-react';
import { useSupportBot } from '../hooks/useSupportBot';
import { ProposedAction } from '@models/approval';

export interface PendingApproval {
  approval_id: string;
  message: string;
  actions: ProposedAction[];
  task_id: string;
  internal?: boolean;
}

export const SupportBotButton = ({
  chat_id,
  currentMessage,
  onReword,
  onSuggestion,
  disabled,
}: {
  chat_id: string;
  currentMessage: string;
  onReword: (message: string) => void;
  onSuggestion: (approval: PendingApproval) => void;
  disabled?: boolean;
}) => {
  const { getNextMessageMutation, rewordMessageMutation } = useSupportBot({
    onNextMessage: onReword,
  });
  const { userDoc } = useAuth();

  if (!userDoc?.roles?.support) return null;

  const handleGetSuggestion = () => {
    getNextMessageMutation.mutate(chat_id, {
      onSuccess: (data) => {
        if (data.approval_id) {
          onSuggestion({
            approval_id: data.approval_id,
            message: data.message,
            actions: data.actions ?? [],
            task_id: data.task_id ?? 'unknown',
            internal: data.internal,
          });
        }
      },
    });
  };

  return (
    <div className="flex items-center gap-2">
      {/* Reword button */}
      <button
        type="button"
        className="flex h-14 w-14 items-center justify-center rounded-full bg-zinc-800 text-white transition-colors hover:bg-zinc-600"
        onClick={() => rewordMessageMutation.mutate(currentMessage)}
        disabled={rewordMessageMutation.isPending}
        title="Reword message"
      >
        {rewordMessageMutation.isPending ? (
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-t-transparent" />
        ) : (
          <PencilIcon className="h-5 w-5" />
        )}
      </button>

      {/* Get suggestion button */}
      <button
        type="button"
        disabled={disabled || getNextMessageMutation.isPending}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-secondary text-white transition-colors hover:bg-brand-secondary-darker disabled:opacity-50"
        onClick={handleGetSuggestion}
        title="Get AI suggestion"
      >
        {getNextMessageMutation.isPending ? (
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-t-transparent" />
        ) : (
          <BotIcon className="h-5 w-5" />
        )}
      </button>
    </div>
  );
};
